import { useState } from "react";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Building, AlertTriangle } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { Company } from "@/types";
import { UserRole as UserRoleEnum } from "@/types/enums";
import { CompanyForm, CompanyFormData } from "./CompanyForm";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert"
;

interface AddCompanyDialogProps {
  onCompanyAdded: (company: Company) => void;
}

const DEFAULT_COMPANY_DATA: CompanyFormData = {
  name: "",
  contact_person: "",
  contact_email: "",
  contact_role: UserRoleEnum.APP_OWNER,
  contact_phone: "",
  address: "",
  domains: [],
  contact_password: ""
};

export const AddCompanyDialog = ({ onCompanyAdded }: AddCompanyDialogProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [signupsDisabled, setSignupsDisabled] = useState(false);
  
  const handleAddCompany = async (formData: CompanyFormData) => {
    try {
      setIsSubmitting(true);
      
      let userCreated = false;
      let userId: string | null = null;
      
      // First, create the user in Supabase Auth if password is provided
      if (formData.contact_email && formData.contact_password) {
        try {
          // Use admin signup to avoid automatic login
          const { data, error: authError } = await supabase.auth.signUp({
            email: formData.contact_email,
            password: formData.contact_password,
            options: {
              data: {
                role: formData.contact_role,
                name: formData.contact_person || formData.contact_email,
                first_name: formData.contact_person ? formData.contact_person.split(' ')[0] : '',
                last_name: formData.contact_person ? formData.contact_person.split(' ').slice(1).join(' ') : '',
              },
              // This is the key change - prevent automatic sign-in after registration
              emailRedirectTo: window.location.origin,
            },
          });
          
          if (authError) {
            
            if (authError.message === "Signups not allowed for this instance") {
              // Set flag to show a warning message
              setSignupsDisabled(true);
            } else {
              toast.error(`Failed to create user: ${authError.message}`);
              setIsSubmitting(false);
              return;
            }
          } else {
            userCreated = true;
            userId = data?.user?.id || null;
            
            // Explicitly create a profile entry for the new user
            if (userId) {
              try {
                // First check if profile already exists to prevent duplicates
                const { data: existingProfile } = await supabase
                  .from('profiles')
                  .select('id')
                  .eq('id', userId)
                  .single();
                
                // Only create profile if it doesn't exist
                if (!existingProfile) {
                  const { error: profileError } = await supabase.rpc('create_profile_if_not_exists', {
                    p_id: userId,
                    p_email: formData.contact_email,
                    p_name: formData.contact_person || formData.contact_email,
                    p_first_name: formData.contact_person ? formData.contact_person.split(' ')[0] : '',
                    p_last_name: formData.contact_person ? formData.contact_person.split(' ').slice(1).join(' ') : '',
                    p_role: formData.contact_role
                  });
                
                  if (profileError) {
                    toast.error('User created but profile setup failed. Contact support.');
                  }
                }                
              } catch {
                // Profile creation error handled silently
              }
            }
          }
        } catch {
          toast.error('Failed to create user account');
          setIsSubmitting(false);
          return;
        }
      }
      
      // Then insert the company into Supabase
      const { data, error } = await supabase
        .from('companies')
        .insert({
          name: formData.name,
          contact_person: formData.contact_person || null,
          contact_email: formData.contact_email,
          contact_role: formData.contact_role,
          contact_phone: formData.contact_phone || null,
          address: formData.address || null,
          domains: formData.domains || []
        })
        .select();
      
      if (error) {
        toast.error('Failed to add company');
        setIsSubmitting(false);
        return;
      }
      
      if (data && data.length > 0) {
        onCompanyAdded(data[0] as Company);
        
        if (userCreated) {
          toast.success("Company and user account created successfully");
        } else if (signupsDisabled && formData.contact_password) {
          toast.success("Company added successfully");
          toast.warning("User account was not created - signups are disabled in this Supabase instance");
        } else {
          toast.success("Company added successfully");
        }
        
        setIsOpen(false);
      }
    } catch {
      toast.error('An error occurred while adding company');
    } finally {
      setIsSubmitting(false);
    }
  };
  
  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>
        <Button variant="secondary" className="flex items-center gap-2">
          <Building className="h-4 w-4" />
          Add Company
        </Button>
      </DialogTrigger>
      
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Add New Company</DialogTitle>
          <DialogDescription>
            Create a new company in ShiftSage
          </DialogDescription>
        </DialogHeader>

        {signupsDisabled && (
          <Alert variant="warning" className="mb-4">
            <AlertTriangle className="h-4 w-4" />
            <AlertTitle>Signups Disabled</AlertTitle>
            <AlertDescription>
              User signups are disabled in this Supabase instance. Companies can be created, but user accounts cannot be automatically provisioned.
            </AlertDescription>
          </Alert>
        )}
        
        <CompanyForm
          initialData={DEFAULT_COMPANY_DATA}
          onSubmit={handleAddCompany}
          onCancel={() => setIsOpen(false)}
          submitLabel="Add Company"
          isSubmitting={isSubmitting}
        />
      </DialogContent>
    </Dialog>
  );
};
